import { cva, type VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { InvoiceStatus } from '@/types';

const badge = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-sm font-medium',
  {
    variants: {
      tone: {
        neutral:
          'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
        accent: 'bg-[var(--accent-soft)] text-[var(--accent)]',
        positive:
          'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
        warning: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
        danger: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-400',
        outline: 'border border-[var(--line-strong)] text-[var(--text-muted)]'
      }
    },
    defaultVariants: { tone: 'neutral' }
  }
);

export function Badge({
  className,
  tone,
  children
}: VariantProps<typeof badge> & { className?: string; children: ReactNode }) {
  return <span className={cn(badge({ tone }), className)}>{children}</span>;
}

const STATUS_TONE: Record<InvoiceStatus, VariantProps<typeof badge>['tone']> = {
  tentative: 'accent',
  draft: 'neutral',
  sent: 'warning',
  paid: 'positive',
  archived: 'outline',
  discarded: 'outline'
};

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  tentative: 'Tentative',
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  archived: 'Archived',
  discarded: 'Discarded'
};

export function StatusBadge({
  status,
  overdue,
  processing
}: {
  status: InvoiceStatus;
  overdue?: boolean;
  /** The client's bank transfer is submitted and clearing. Wins over overdue. */
  processing?: boolean;
}) {
  if (processing && status === 'sent') {
    return <Badge tone="accent">Processing</Badge>;
  }
  if (overdue && status === 'sent') {
    return <Badge tone="danger">Overdue</Badge>;
  }
  return <Badge tone={STATUS_TONE[status] ?? 'neutral'}>{STATUS_LABEL[status] ?? status}</Badge>;
}
